/**
 * @file index.tsx
 *
 * @fileoverview Renders an Accordion.
 */
import {
  Content,
  Header,
  Item,
  Root,
  Trigger,
} from "@radix-ui/react-accordion";
import React from "react";
import ArrowDownIcon from "@jq-icons/ArrowDown";
import FlexItems from "../06-layout/03-flex";
import FlexItem from "../06-layout/03-flex/02-flex-item";
import styles from "./accordion.module.scss";

export interface AccordionButtonProps extends Pick<AccordionProps, "children"> {
  /**
   * The down icon title.
   *
   * @default 'down'
   */
  iconTitle?: string;
}

const AccordionButton = ({
  children,
  iconTitle = "down",
  ...props
}: AccordionButtonProps) => (
  <Header className={styles.accordionButton} {...props}>
    <Header asChild={true}>
      <Trigger>
        <FlexItems justifyContent="space-between">
          <FlexItem>{children}</FlexItem>
          <FlexItem>
            <ArrowDownIcon color="gray7" title={iconTitle ?? "down"} />
          </FlexItem>
        </FlexItems>
      </Trigger>
    </Header>
  </Header>
);

const AccordionPanel = ({
  children,
  ...props
}: Pick<AccordionProps, "children">) => {
  return (
    <div className={styles.accordionPanel} {...props}>
      <Content>{children}</Content>
    </div>
  );
};
export interface AccordionProps {
  /**
   * The contents of the accordion.
   */
  children: React.ReactNode;
  /**
   * If the accordion allows to be totally collapsible.
   *
   * @default "false"
   */
  collapsible?: boolean;
  /**
   * If the accordion allows multiple items open at once.
   *
   * @default "false"
   */
  multipleItems?: boolean;
}

/**
 * An accordion allows showing multiple panels of information together. Usually one opens at a time but also allows multiple to be open at once.
 * Follows the ARIA spec https://www.w3.org/TR/wai-aria-practices-1.2/#accordion
 */
export const Accordion = ({
  children,
  collapsible = false,
  multipleItems = false,
}: AccordionProps) => (
  <Root
    type={multipleItems ? "multiple" : "single"}
    // @ts-ignore
    collapsible={collapsible.toString()}
  >
    {children}
  </Root>
);

export default Accordion;
export { Item as AccordionItem, AccordionButton, AccordionPanel };
